import { useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, ContactShadows } from "@react-three/drei";
import * as THREE from "three";

const Mat = ({ color }) => <meshStandardMaterial color={color} roughness={0.55} metalness={0.05} />;

function Face({ url }) {
  const [tex, setTex] = useState(null);
  useEffect(() => {
    if (!url) { setTex(null); return; }
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("use-credentials");
    loader.load(url, (t) => { t.colorSpace = THREE.SRGBColorSpace; setTex(t); }, undefined, () => setTex(null));
  }, [url]);
  if (!tex) return null;
  return (
    <mesh position={[0, 1.6, 0.104]}>
      <circleGeometry args={[0.088, 48]} />
      <meshBasicMaterial map={tex} />
    </mesh>
  );
}

function Mannequin({ m, colors, photoUrl }) {
  const s = m.height / 170;
  const chestW = m.chest / 90;
  const waistW = m.waist / 72;
  const hipW = m.hip / 96;
  const masc = m.body_type === "maskulin";
  const shoulder = (masc ? 0.25 : 0.22) * chestW;
  const skin = m.skin || "#D9A882";
  const top = colors.dress || colors.top || "#E9E4DA";
  const bottom = colors.dress ? skin : colors.bottom || "#2B2B30";
  const arms = colors.outer || (colors.dress ? skin : top);
  const shoes = colors.shoes || "#F2F0EB";

  return (
    <group scale={[1, s, 1]}>
      {[-1, 1].map((d) => (
        <group key={d}>
          <mesh position={[d * 0.095 * hipW, 0.04, 0.04]}><boxGeometry args={[0.1, 0.08, 0.26]} /><Mat color={shoes} /></mesh>
          <mesh position={[d * 0.095 * hipW, 0.46, 0]}><capsuleGeometry args={[0.065 * Math.max(hipW, 0.9), 0.7, 8, 16]} /><Mat color={bottom} /></mesh>
          <mesh position={[d * (shoulder + 0.03), 1.12, 0]} rotation={[0, 0, d * 0.1]}><capsuleGeometry args={[0.045, 0.52, 8, 16]} /><Mat color={arms} /></mesh>
          <mesh position={[d * (shoulder + 0.065), 0.79, 0]}><sphereGeometry args={[0.045, 16, 16]} /><Mat color={skin} /></mesh>
        </group>
      ))}
      <mesh position={[0, 0.9, 0]} scale={[hipW * (masc ? 0.95 : 1.1), 0.62, 0.72]}><sphereGeometry args={[0.17, 32, 32]} /><Mat color={colors.dress || bottom === skin ? top : bottom} /></mesh>
      <mesh position={[0, 1.02, 0]}><cylinderGeometry args={[0.13 * chestW, 0.12 * waistW, 0.2, 32]} /><Mat color={top} /></mesh>
      <mesh position={[0, 1.24, 0]} scale={[chestW * (masc ? 1.1 : 1), 1, 0.66]}><capsuleGeometry args={[0.15, 0.18, 8, 32]} /><Mat color={colors.outer || top} /></mesh>
      {colors.dress && (
        <mesh position={[0, 0.7, 0]}><cylinderGeometry args={[0.15 * waistW, 0.27 * hipW, 0.5, 32, 1, true]} /><meshStandardMaterial color={top} side={THREE.DoubleSide} roughness={0.6} /></mesh>
      )}
      <mesh position={[0, 1.45, 0]}><cylinderGeometry args={[0.045, 0.05, 0.1, 16]} /><Mat color={skin} /></mesh>
      <mesh position={[0, 1.6, 0]}><sphereGeometry args={[0.105, 32, 32]} /><Mat color={skin} /></mesh>
      <Face url={photoUrl} />
    </group>
  );
}

export default function Avatar3D({ measurements, colors = {}, photoUrl, autoRotate = true }) {
  return (
    <div className="relative h-full w-full" data-testid="avatar-3d-canvas">
      <Canvas camera={{ position: [0, 1.15, 2.9], fov: 38 }} dpr={[1, 2]}>
        <color attach="background" args={["#121215"]} />
        <ambientLight intensity={0.55} />
        <directionalLight position={[2, 4, 3]} intensity={1.4} />
        <directionalLight position={[-3, 2, -2]} intensity={0.5} color="#D4AF37" />
        <Mannequin m={measurements} colors={colors} photoUrl={photoUrl} />
        <ContactShadows position={[0, 0, 0]} opacity={0.55} scale={3} blur={2.4} far={2} />
        <OrbitControls target={[0, 0.95, 0]} enablePan={false} minDistance={1.6} maxDistance={4.5} autoRotate={autoRotate} autoRotateSpeed={1.2} />
      </Canvas>
      <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Ziehen zum Drehen · Scrollen zum Zoomen</span>
    </div>
  );
}

export function colorsFromItems(items = []) {
  const map = { Oberteil: "top", Hose: "bottom", Schuhe: "shoes", Jacke: "outer", Kleid: "dress" };
  const c = {};
  items.forEach((it) => { if (map[it.category]) c[map[it.category]] = it.color; });
  return c;
}
